import { Button, cn, Label, Tooltip, TooltipContent, TooltipTrigger } from '@1trading/react';
import type { ReactNode } from 'react';

interface ContextTooltipProps {
  children: ReactNode;
  description: ReactNode;
  /** Renders the term as the label of this control */
  htmlFor?: string;
  className?: string;
}

/**
 * A term that explains itself on hover or keyboard focus, like the exchange's market stats.
 * The trigger is a focusable span, not a button: a disabled fieldset (read-only views) would disable a button and
 * hide the explanation, and a button inside a label stops clicks on the term reaching the labelled control.
 * The content is rendered beside the label, not inside it, so it never becomes part of the control's name.
 */
export const ContextTooltip = ({ children, description, htmlFor, className }: ContextTooltipProps) => {
  const trigger = (
    <TooltipTrigger asChild>
      <Button asChild className={cn('cursor-help whitespace-normal text-left hover:after:border-dashed', className)} size="contents" variant="contextual">
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus opens the explanation, which is the trigger's only job */}
        <span tabIndex={0}>{children}</span>
      </Button>
    </TooltipTrigger>
  );
  return (
    <Tooltip>
      {htmlFor ? (
        <Label className="w-fit text-sm" htmlFor={htmlFor}>
          {trigger}
        </Label>
      ) : (
        trigger
      )}
      <TooltipContent className="max-w-72 text-xs leading-relaxed" contain>
        {description}
      </TooltipContent>
    </Tooltip>
  );
};
ContextTooltip.displayName = 'ContextTooltip';
